'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedAvifSource } from '@/components/tools/avif-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { analyzeAvifAlpha, formatFileSize } from '@/lib/browser-avif-tools';

type AlphaView = 'source' | 'alpha' | 'alpha-mask';

export default function BrowserAvifAlphaChecker() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource, isSupported } = useLoadedAvifSource();
  const [selectedView, setSelectedView] = useState<AlphaView>('alpha');
  const [alphaUrl, setAlphaUrl] = useState<string | null>(null);
  const [alphaMaskUrl, setAlphaMaskUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [transparencySummary, setTransparencySummary] = useState<string>('Not analyzed yet');
  const [transparencyStats, setTransparencyStats] = useState<{
    opaquePixels: number;
    transparentPixels: number;
    translucentPixels: number;
    opaquePercent: number;
    transparentPercent: number;
    translucentPercent: number;
    hasTransparency: boolean;
  } | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const currentPreviewUrl = useMemo(() => {
    if (selectedView === 'source') {
      return source?.previewUrl ?? null;
    }

    if (selectedView === 'alpha-mask') {
      return alphaMaskUrl;
    }

    return alphaUrl;
  }, [alphaMaskUrl, alphaUrl, selectedView, source]);

  useEffect(() => {
    return () => {
      [alphaUrl, alphaMaskUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [alphaMaskUrl, alphaUrl]);

  function resetOutputState() {
    [alphaUrl, alphaMaskUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setAlphaUrl(null);
    setAlphaMaskUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
    setTransparencySummary('Not analyzed yet');
    setTransparencyStats(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be opened.',
      );
    }
  }

  async function runAnalysis() {
    if (!source) {
      setErrorMessage('Choose one AVIF first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'avif-alpha-checker',
        'Credits are not available for another AVIF alpha analysis right now.',
      );
      const result = await analyzeAvifAlpha(source.file);
      const nextAlphaUrl = URL.createObjectURL(result.alphaBlob);
      const nextAlphaMaskUrl = URL.createObjectURL(result.alphaMaskBlob);

      resetOutputState();
      setAlphaUrl(nextAlphaUrl);
      setAlphaMaskUrl(nextAlphaMaskUrl);
      setSelectedView('alpha');
      setDownloadUrl(nextAlphaUrl);
      setDownloadName(`${source.file.name.replace(/\.avif$/i, '') || source.file.name}-alpha.png`);
      setResultSize(result.alphaBlob.size);
      setTransparencyStats(result.transparency);
      setTransparencySummary(
        result.transparency.hasTransparency
          ? `${result.transparency.transparentPercent}% transparent • ${result.transparency.translucentPercent}% translucent`
          : 'No meaningful transparency detected',
      );
      setCreditMessage(formatToolCreditSuccess('AVIF alpha analysis', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF alpha channel could not be analyzed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  function changeView(nextView: AlphaView) {
    setSelectedView(nextView);

    if (nextView === 'source') {
      setDownloadUrl(null);
      setDownloadName(null);
      setResultSize(null);
      return;
    }

    const nextUrl = nextView === 'alpha-mask' ? alphaMaskUrl : alphaUrl;

    setDownloadUrl(nextUrl);
    setDownloadName(
      source
        ? `${source.file.name.replace(/\.avif$/i, '') || source.file.name}-${nextView}.png`
        : null,
    );
    setResultSize(nextView === 'alpha-mask' ? null : resultSize);
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Check AVIF transparency and export the alpha view</h2>
          <p>
            Confirm whether an AVIF really contains meaningful alpha, inspect the alpha render, and
            export the current view as PNG.
          </p>
        </div>
        <span className="tool-live-badge">Alpha</span>
      </div>

      {!isSupported ? (
        <p className="support-feedback support-feedback-error">
          This browser cannot process AVIF locally yet. Use a modern Chromium or Safari build with
          ImageDecoder and OffscreenCanvas support.
        </p>
      ) : null}

      <div className="tool-live-routes" aria-label="AVIF alpha views">
        {[
          ['source', 'RGB'],
          ['alpha', 'Alpha'],
          ['alpha-mask', 'Alpha mask'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`tool-live-route ${selectedView === id ? 'tool-live-route-active' : ''}`}
            onClick={() => changeView(id as AlphaView)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            disabled={!isSupported}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one AVIF or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still AVIF only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/avif,.avif"
            className="tool-live-file-input"
            disabled={!isSupported}
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing || !isSupported}
              onClick={() => void runAnalysis()}
              type="button"
            >
              {isProcessing ? 'Analyzing…' : 'Analyze alpha'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download PNG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Transparency summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Status</strong>
              <span>{transparencySummary}</span>
            </div>
            <div>
              <strong>Opaque pixels</strong>
              <span>
                {transparencyStats ? `${transparencyStats.opaquePercent}%` : 'Not analyzed yet'}
              </span>
            </div>
            <div>
              <strong>Transparent pixels</strong>
              <span>
                {transparencyStats
                  ? `${transparencyStats.transparentPercent}%`
                  : 'Not analyzed yet'}
              </span>
            </div>
            <div>
              <strong>Translucent pixels</strong>
              <span>
                {transparencyStats
                  ? `${transparencyStats.translucentPercent}%`
                  : 'Not analyzed yet'}
              </span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div
          className="tool-live-preview-frame"
          style={selectedView === 'source' ? undefined : { background: '#ffffff' }}
        >
          {currentPreviewUrl ? (
            <img alt="AVIF alpha analysis preview" src={currentPreviewUrl} />
          ) : (
            <div className="tool-editor-empty">
              <strong>No alpha preview yet</strong>
              <span>Analyze one AVIF to inspect the alpha render.</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
